import type { PostgrestError } from '@supabase/supabase-js';
import type { AccountIntelRegistry } from '../types/crm';
import { getDbSupabase } from './supabase';
import { formatSalespersonCode } from './accountCollection';

export interface ClientPayload {
  company_name: string;
  code: string;
  client_type: string;
  owner_id: string;
  main_phone: string;
  website: string;
  uen: string;
  description: string;
  address: string;
  district: string;
}

export type ClientSavePayload = Omit<ClientPayload, 'owner_id'> & {
  parent_client_id?: string | null;
  intel_profile_json?: AccountIntelRegistry | null;
};

/**
 * Push parent account type onto all brand / sub-company rows.
 */
export async function syncBrandClientTypes(
  parentClientId: string,
  clientType: string,
): Promise<{ error: PostgrestError | null; updated: number }> {
  const type = clientType.trim();
  if (!parentClientId || !type) return { error: null, updated: 0 };

  const { data, error } = await getDbSupabase()
    .from('clients')
    .update({ client_type: type })
    .eq('parent_client_id', parentClientId)
    .select('id');

  if (error) return { error, updated: 0 };
  return { error: null, updated: data?.length ?? 0 };
}

/**
 * Save client row. For new accounts, `newSalespersonCode` is stored in clients.owner_id
 * (NAV salesperson code — not a portal user id). IT Management may omit salesperson.
 */
export async function saveClientRecord(
  payload: ClientSavePayload,
  options: {
    clientId?: string;
    /** Required for new CRM accounts — salesperson code assigned to the account. */
    newSalespersonCode?: string;
    parentClientId?: string | null;
    /** When true, cascade client_type to brands under this account. */
    syncBrandTypes?: boolean;
    /** IT accounts may omit salesperson (owner_id stored as empty). */
    allowEmptyOwner?: boolean;
  } = {},
): Promise<{ error: PostgrestError | null; clientId?: string }> {
  if (options.clientId) {
    const { parent_client_id: _drop, ...updatePayload } = payload;
    const { error: updateError } = await getDbSupabase()
      .from('clients')
      .update(updatePayload)
      .eq('id', options.clientId);
    if (updateError) return { error: updateError, clientId: options.clientId };

    if (options.syncBrandTypes && payload.client_type?.trim()) {
      const { error: syncError } = await syncBrandClientTypes(
        options.clientId,
        payload.client_type,
      );
      if (syncError) return { error: syncError, clientId: options.clientId };
    }

    return { error: null, clientId: options.clientId };
  }

  const owner_id = formatSalespersonCode(options.newSalespersonCode ?? '');
  if (!owner_id && !options.allowEmptyOwner) {
    return {
      error: {
        message: 'Salesperson code is required to create an account.',
        details: '',
        hint: '',
        code: 'P0001',
        name: 'PostgrestError',
      } as PostgrestError,
    };
  }

  const insertBase = {
    ...payload,
    owner_id: owner_id || '',
    ...(options.parentClientId ? { parent_client_id: options.parentClientId } : {}),
  };

  const { data, error: insertError } = await getDbSupabase()
    .from('clients')
    .insert(insertBase)
    .select('id')
    .single();

  if (insertError) return { error: insertError };
  return { error: null, clientId: data?.id != null ? String(data.id) : undefined };
}
